/**
 * @deprecated This context wraps legacy profile data in the old `GetUserProfileApiResponse` shape.
 * For new code, use `useGetProfile()` from `src/features/profile/hooks/useProfile.ts`
 * which returns a `Profile` entity with typed getters and business methods.
 *
 * This context is still used by 33+ consumers. Migrate gradually.
 */
import {PropsWithChildren} from "react";
import {createContext} from "../create-context";
import {FullPageLoader} from "../../components/full-page-loader";
import {Button, Result} from "antd";
import {useGetUserProfile} from "../../features/profile/hooks/useProfile";
import {GetUserProfileApiResponse} from "../../react-query-hooks/profile/types";

type TGetProfileState = {
  profileData: GetUserProfileApiResponse;
};

type TGetProfileContextState = {
  state: TGetProfileState;
};

const [useContext, ContextProvider] = createContext<TGetProfileContextState>();

export const useGetProfileContext = useContext;

export const ProfileFixtureProvider = ({
  children,
  profileData,
}: PropsWithChildren<{profileData: GetUserProfileApiResponse}>) => (
  <ContextProvider value={{state: {profileData}}}>{children}</ContextProvider>
);

export const GetProfileProvider = ({children}: PropsWithChildren) => {
  const getProfileQuery = useGetUserProfile();

  // BUG FIX: Only show full-page spinner on initial load when there is no cached data.
  // Previously used `isLoading` which is also true during refetches, causing the form
  // to unmount and lose all user input. Now we keep children mounted with stale data
  // while a background refetch is in progress.
  if (getProfileQuery.isLoading && !getProfileQuery.data) {
    return <FullPageLoader />;
  }

  if (getProfileQuery.isError && !getProfileQuery.data) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching profile"
        extra={
          <Button type="primary" size="small" onClick={() => getProfileQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const state: TGetProfileState = {
    profileData: getProfileQuery.data!,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
